<template>
  <div class="ui-docupload">
    <div class="ui-docupload__prompt">{{ block.props.prompt }}</div>

    <!-- ── Phase: dropzone / paste ─────────────────────────── -->
    <template v-if="interactive && phase === 'upload'">
      <template v-if="inputMode === 'file'">
        <div
          class="ui-docupload__dropzone"
          :class="{ 'ui-docupload__dropzone--over': dragOver, 'ui-docupload__dropzone--busy': uploading }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
          @click="!uploading && $refs.fileInput.click()"
        >
          <template v-if="uploading">
            <span class="ui-docupload__spinner" aria-hidden="true" />
            <span>Uploading {{ pendingName }}…</span>
          </template>
          <template v-else>
            <v-icon size="22" color="indigo">mdi-file-upload-outline</v-icon>
            <span>Drop a file here, or click to choose</span>
            <span class="ui-docupload__hint">{{ acceptLabel }}</span>
          </template>
        </div>
        <input
          ref="fileInput"
          type="file"
          class="ui-docupload__file-input"
          :accept="acceptAttr"
          @change="onPick"
        />
        <button
          type="button"
          class="ui-docupload__mode-toggle"
          :disabled="uploading"
          @click="inputMode = 'paste'"
        >…or paste the text instead</button>
      </template>

      <!-- Paste mode: the text is persisted server-side as a .txt via
           the SAME /agent/upload?persist=true call the file path uses,
           so everything downstream (chain, review, provenance link) is
           identical — the engine only ever sees a file_id. -->
      <template v-else>
        <!-- v-focus: the textarea is inserted by the mode toggle, so
             focus lands in it the moment "…or paste the text" is clicked. -->
        <textarea
          v-model="pastedText"
          v-focus
          class="ui-field-input ui-docupload__paste-input"
          rows="8"
          :disabled="uploading"
          placeholder="Paste the role description, advert or profile text here…"
          @keydown.ctrl.enter.prevent="submitPastedText"
          @keydown.meta.enter.prevent="submitPastedText"
        />
        <div class="ui-docupload__actions">
          <button
            type="button"
            class="ui-docupload__confirm"
            :disabled="!pasteReady || uploading"
            @click="submitPastedText"
          >
            <template v-if="uploading">Processing…</template>
            <template v-else>Use this text</template>
          </button>
          <button
            type="button"
            class="ui-docupload__discard"
            :disabled="uploading"
            @click="inputMode = 'file'"
          >Upload a file instead</button>
        </div>
      </template>
      <div v-if="error" class="ui-docupload__error">{{ error }}</div>
    </template>

    <!-- ── Phase: review ───────────────────────────────────── -->
    <template v-else-if="interactive && phase === 'review' && review">
      <!-- Duplicate warning: existing active docs at this position.
           Confirming with these visible IS the "create anyway" consent. -->
      <div v-if="review.duplicates && review.duplicates.length" class="ui-docupload__duplicates">
        <div class="ui-docupload__dup-head">
          <v-icon size="14" color="amber darken-3">mdi-alert</v-icon>
          <span class="ui-docupload__dup-title">
            A {{ review.duplicates[0].doc_type || 'document' }} already exists for this position — creating will add another.
          </span>
          <button
            v-if="compareAvailable && review.duplicates.length >= 2"
            type="button"
            class="ui-docupload__dup-compare"
            :title="`Compare ${review.duplicates.length} documents side-by-side`"
            @click="compareDuplicates"
          >
            <v-icon size="12" color="amber darken-4">mdi-compare</v-icon>
            Compare
          </button>
        </div>
        <!-- Grouped by job family (shared helper — same headings as
             search results and the create gates). -->
        <template v-for="row in duplicateRows">
          <div
            v-if="row.type === 'header'"
            :key="`family-${row.family}`"
            class="ui-group-label ui-docupload__dup-group"
          >{{ row.family }}<span
            v-if="row.description"
            class="ui-group-label__desc"
          > — {{ row.description }}</span></div>
          <!-- Rows carry the DS MatchItem anatomy: title + other-
               hierarchy context on the top line, status dot on the
               second. The RM reference is not shown — actions carry it. -->
          <div
            v-else
            :key="row.doc.reference"
            class="ui-docupload__dup-row"
            role="button"
            tabindex="0"
            @click="openDuplicate($event, row.doc)"
            @keyup.enter.self="openDuplicate($event, row.doc)"
          >
            <div class="ui-docupload__dup-text">
              <span class="ui-docupload__dup-name">
                <span class="ui-docupload__dup-name-text">{{ row.doc.name }}</span>
                <span
                  v-if="row.doc.other_hierarchies && row.doc.other_hierarchies.length"
                  class="ui-docupload__dup-ctx"
                >{{ row.doc.other_hierarchies.join(' · ') }}</span>
              </span>
              <!-- Second line: status dot + the row's quiet actions
                   (Open, and View for the quick-view popover). -->
              <span
                v-if="row.doc.status || (openAvailable && row.doc.doc_id) || row.doc.portal_url"
                class="ui-docupload__dup-sub"
              >
                <template v-if="row.doc.status">
                  <span
                    class="ui-docupload__dup-dot"
                    :style="{ background: statusColor(row.doc.status) }"
                    aria-hidden="true"
                  />
                  <span>{{ row.doc.status }}</span>
                </template>
                <button
                  v-if="openAvailable && row.doc.doc_id"
                  type="button"
                  class="ui-docupload__dup-open"
                  :title="`Open ${row.doc.reference}`"
                  @click.stop="openDuplicateDoc(row.doc)"
                  @keyup.enter.stop
                >Open</button>
                <a
                  v-else-if="row.doc.portal_url"
                  :href="row.doc.portal_url"
                  target="_blank"
                  rel="noopener"
                  class="ui-docupload__dup-open"
                  :title="`Open ${row.doc.reference} in RoleMapper`"
                  @click.stop
                >Open</a>
                <button
                  type="button"
                  class="ui-docupload__dup-view"
                  :title="`Quick view ${row.doc.reference}`"
                  @click.stop="openDuplicate($event, row.doc)"
                  @keyup.enter.stop
                >
                  <v-icon size="13" class="ui-docupload__dup-view-icon">mdi-eye-outline</v-icon>
                  View
                </button>
              </span>
              <ul
                v-if="row.doc.match_reasons && row.doc.match_reasons.length && isWhyOpen(row.doc)"
                class="ui-why-list"
              >
                <li
                  v-for="(reason, ri) in row.doc.match_reasons"
                  :key="ri"
                  class="ui-why-item"
                >
                  <v-icon size="13" class="ui-why-check">mdi-check</v-icon>
                  <span>{{ reason }}</span>
                </li>
              </ul>
            </div>
            <!-- Trailing column: match pill with the "Why it matches"
                 toggle beneath it. The toggle never fires the row. -->
            <span
              v-if="row.doc.match || (row.doc.match_reasons && row.doc.match_reasons.length)"
              class="ui-docupload__dup-trail"
            >
              <span
                v-if="row.doc.match"
                class="ui-match"
                :class="`ui-match--${row.doc.match}`"
                :title="matchTitle(row.doc.match)"
              >{{ matchLabel(row.doc.match) }}</span>
              <button
                v-if="row.doc.match_reasons && row.doc.match_reasons.length"
                type="button"
                class="ui-why-toggle"
                :class="{ 'ui-why-toggle--open': isWhyOpen(row.doc) }"
                :aria-expanded="isWhyOpen(row.doc) ? 'true' : 'false'"
                @click.stop="toggleWhy(row.doc)"
                @keyup.enter.stop
              >
                <v-icon size="13">mdi-help-circle-outline</v-icon>
                Why it matches
              </button>
            </span>
          </div>
        </template>
      </div>

      <!-- Mapped content and captured data are separate reads —
           pill tabs (DS UploadMapCard) with a right-aligned meta line. -->
      <div class="ui-docupload__tabs" role="tablist">
        <button
          type="button"
          class="ui-docupload__tab"
          :class="{ 'ui-docupload__tab--active': activeTab === 'content' }"
          @click="activeTab = 'content'"
        >
          Mapped sections
        </button>
        <button
          type="button"
          class="ui-docupload__tab"
          :class="{ 'ui-docupload__tab--active': activeTab === 'data' }"
          @click="activeTab = 'data'"
        >
          Raw data
        </button>
        <span class="ui-docupload__tabs-meta">
          <template v-if="activeTab === 'content'">{{ editableParts.length }} mapped</template>
          <template v-else-if="review.original && review.original.filename">{{ review.original.filename }}</template>
          <template v-else>As Atlas read it</template>
        </span>
      </div>

      <!-- Generated content is EDITABLE before confirming: section
           titles are fixed, the content is the user's to correct. Edits
           ride the confirm action and replace the chain's rows for those
           parts in the created document. -->
      <template v-if="activeTab === 'content'">
        <div class="ui-docupload__parts">
          <!-- Each mapped section is its own quiet card (DS
               UploadMapCard field row): name + chevron, a one-line
               preview while collapsed, editors under a hairline when
               open. All sections start expanded. -->
          <div
            v-for="part in editableParts"
            :key="part.key"
            class="ui-docupload__part"
            :class="{ 'ui-docupload__part--open': part.open }"
          >
            <button
              type="button"
              class="ui-docupload__part-head"
              :aria-expanded="part.open ? 'true' : 'false'"
              @click="part.open = !part.open"
            >
              <span class="ui-docupload__part-row">
                <span class="ui-docupload__part-name">{{ part.name }}</span>
                <v-icon size="16" class="ui-docupload__part-chevron">
                  {{ part.open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </span>
              <span v-if="!part.open" class="ui-docupload__part-preview">
                {{ partPreview(part) }}
              </span>
            </button>
            <div v-if="part.open" class="ui-docupload__part-body">
              <!-- No part identity (or display-capped rows): read-only,
                   editing a lossy copy would silently delete content. -->
              <template v-if="!part.editable">
                <div v-if="part.rows.length === 1" class="ui-docupload__part-text">{{ part.rows[0].text }}</div>
                <ul v-else class="ui-docupload__items">
                  <li v-for="(row, ri) in part.rows" :key="ri">{{ row.text }}</li>
                </ul>
                <div v-if="part.items_truncated" class="ui-docupload__truncated">
                  Showing the first entries — the full list is created in the document.
                </div>
              </template>
              <!-- Row editor: drag handle to reorder, click a row to edit
                   it, add only where the template allows multiple rows. -->
              <template v-else-if="part.multi || part.rows.length > 1">
                <draggable
                  v-model="part.rows"
                  handle=".ui-docupload__drag"
                  :animation="150"
                >
                  <div v-for="(row, ri) in part.rows" :key="row.id" class="ui-docupload__row">
                    <span class="ui-docupload__drag" title="Drag to reorder">
                      <v-icon size="16" color="grey">mdi-drag-vertical</v-icon>
                    </span>
                    <textarea
                      v-if="row.editing"
                      v-model="row.text"
                      v-focus
                      rows="2"
                      class="ui-field-input ui-docupload__row-input"
                      @blur="endRowEdit(part, row)"
                      @keydown.esc="$event.target.blur()"
                    />
                    <div
                      v-else
                      class="ui-docupload__row-static"
                      role="button"
                      tabindex="0"
                      title="Click to edit"
                      @click="row.editing = true"
                      @keyup.enter="row.editing = true"
                    >{{ row.text }}</div>
                    <button
                      type="button"
                      class="ui-docupload__row-btn"
                      title="Remove"
                      @click="part.rows.splice(ri, 1)"
                    ><v-icon size="14">mdi-close</v-icon></button>
                  </div>
                </draggable>
                <button
                  v-if="part.multi"
                  type="button"
                  class="ui-docupload__add-row"
                  @click="addRow(part)"
                >
                  <v-icon size="14" color="indigo">mdi-plus</v-icon>
                  Add entry
                </button>
              </template>
              <!-- Single-value part: static text, click to edit (same
                   contract as the multi-row editor). -->
              <template v-else>
                <textarea
                  v-if="part.rows[0].editing"
                  v-model="part.rows[0].text"
                  v-focus
                  rows="3"
                  class="ui-field-input ui-docupload__row-input"
                  @blur="endRowEdit(part, part.rows[0])"
                  @keydown.esc="$event.target.blur()"
                />
                <div
                  v-else
                  class="ui-docupload__row-static"
                  :class="{ 'ui-docupload__row-static--empty': !part.rows[0].text.trim() }"
                  role="button"
                  tabindex="0"
                  title="Click to edit"
                  @click="part.rows[0].editing = true"
                  @keyup.enter="part.rows[0].editing = true"
                >{{ part.rows[0].text.trim() || 'Click to add content' }}</div>
              </template>
            </div>
          </div>
          <div v-if="review.truncated" class="ui-docupload__truncated">
            Showing a summary — the full draft is created in the document.
          </div>
        </div>
      </template>

      <template v-else>
        <div v-if="reviewAttributes.length" class="ui-docupload__attrs">
          <div v-for="(a, ai) in reviewAttributes" :key="ai" class="ui-docupload__attr">
            <span class="ui-docupload__attr-label">{{ a.label }}</span>
            <span class="ui-docupload__attr-value">{{ a.value }}</span>
          </div>
        </div>
        <div v-else class="ui-docupload__truncated">No additional data was captured.</div>
        <template v-if="review.source_text">
          <div class="ui-docupload__source-label">Extracted text</div>
          <div class="ui-docupload__source-text">{{ review.source_text }}</div>
          <div v-if="review.source_text_truncated" class="ui-docupload__truncated">
            Showing the beginning of the extracted text.
          </div>
        </template>
        <div v-if="review.original && review.original.filename" class="ui-docupload__original">
          <v-icon size="14" color="grey darken-1">{{ originalIcon }}</v-icon>
          <span>Source: {{ review.original.filename }}</span>
        </div>
      </template>

      <div class="ui-docupload__actions">
        <button type="button" class="ui-docupload__confirm" @click="confirm">
          {{ block.props.confirm_label || 'Create document' }}
        </button>
        <button type="button" class="ui-docupload__discard" @click="discard">
          Discard
        </button>
      </div>
    </template>

    <!-- ── Phase: creating (goes inert once the created-document
         card arrives as a newer message) ──────────────────── -->
    <template v-else-if="interactive && phase === 'creating'">
      <div class="ui-docupload__creating">
        <span class="ui-docupload__spinner" aria-hidden="true" />
        <span>
          Creating the document<template v-if="creatingFilename"> from {{ creatingFilename }}</template>…
          it will appear here when ready.
        </span>
      </div>
    </template>

    <div v-else-if="!interactive" class="ui-docupload__inert">
      <template v-if="chosenLabel">{{ chosenLabel }}</template>
      <template v-else>This step is no longer active.</template>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, reactive, ref } from 'vue';
import axios from 'axios';
import draggable from 'vuedraggable';
import {
  docStatusColor,
  groupDocRowsByFamily,
  matchTierLabel,
  matchTierTitle,
} from '@rm-ui/agent/uiBlockSpec';

/**
 * `doc_upload` ui_block — import a document from a file. Two
 * engine-emitted phases share this component:
 *
 *  - `upload`: drag-drop / picker. The file goes to
 *    `/agent/upload?persist=true` (extracted text for the chain lives
 *    server-side; the original is stored as a platform file record for
 *    provenance), then the card fires `uploaded` with the slim
 *    `{ file_id, filename, kind }` payload.
 *  - `review`: the drafted content + captured attribute values + the
 *    original file + any duplicate warnings, with Create / Discard
 *    actions.
 *
 * All actions ride the standard workflow-action channel.
 */
export default defineComponent({
  name: 'UiBlockDocUpload',
  components: { draggable },
  directives: {
    // Focus a row's textarea the moment click-to-edit swaps it in.
    focus: { inserted: (el) => el.focus() },
  },
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    /** Friendly label for inert replay cards. */
    chosenLabel: { type: String, default: null },
    /** True when the host page can open the standard document view. */
    openAvailable: { type: Boolean, default: false },
    /** True when the host page can run the standard document compare. */
    compareAvailable: { type: Boolean, default: false },
  },
  emits: ['action', 'open-summary', 'open-document', 'compare-documents'],
  setup(props, { emit }) {
    const dragOver = ref(false);
    const uploading = ref(false);
    const pendingName = ref('');
    const error = ref('');
    const activeTab = ref('content');

    // Upload-phase input mode: 'file' (dropzone, the default) or
    // 'paste' (textarea). Paste covers the "I have the text of a job
    // advert / role outline, not a file" case — the text is wrapped in
    // a synthetic .txt File and sent down the identical upload path.
    const inputMode = ref('file');
    const pastedText = ref('');
    // Below this the chain has nothing to work with — the button stays
    // disabled so a stray line can't kick off a doomed run.
    const PASTE_MIN_CHARS = 40;
    const pasteReady = computed(
      () => pastedText.value.trim().length >= PASTE_MIN_CHARS,
    );

    const phase = computed(() => props.block.props.phase || 'upload');
    const review = computed(() => props.block.props.review || null);

    // Duplicates render grouped under family headings (shared helper —
    // identical treatment to search results and the create gates).
    const duplicateRows = computed(() =>
      groupDocRowsByFamily(review.value?.duplicates || []),
    );

    // Editable working copy of the generated content. Rows are OBJECTS
    // ({ id, text, editing }) because Vue 2 can't observe direct
    // array-index writes — and drag reordering needs a stable key. Rows
    // render as static text until clicked (editing = true); the
    // original texts are kept so confirm() only sends parts the user
    // actually changed. Parts without a part_id (or whose rows were
    // display-capped) stay read-only — see uiBlockSpec.
    let rowUid = 0;
    const editableParts = reactive(
      ((props.block.props.review && props.block.props.review.parts) || []).map((p, i) => {
        const texts = p.items && p.items.length ? [...p.items] : p.text ? [p.text] : [];
        return {
          key: i,
          part_id: p.part_id || null,
          name: p.name,
          multi: p.multi === true,
          items_truncated: p.items_truncated === true,
          editable: !!p.part_id && p.items_truncated !== true,
          open: true,
          rows: texts.map((t) => ({ id: rowUid++, text: t, editing: false })),
          original: texts,
        };
      }),
    );

    function addRow(part) {
      // A fresh entry opens straight into editing.
      part.rows.push({ id: rowUid++, text: '', editing: true });
    }

    /** Collapsed-card preview (DS UploadMapCard): the value itself for
     * a single-entry section, an entry count otherwise. */
    function partPreview(part) {
      if (part.rows.length === 1) {
        const text = String(part.rows[0].text ?? '').trim();
        if (text) return text;
      }
      return `${part.rows.length} ${part.rows.length === 1 ? 'entry' : 'entries'}`;
    }

    function endRowEdit(part, row) {
      row.editing = false;
      // A row left blank would be dropped at confirm anyway — remove it
      // now so the list matches what will be created. Single-value
      // parts keep their (blank) row: removing it would leave nothing
      // to click, and the blank still means "content removed".
      if (!String(row.text ?? '').trim() && (part.multi || part.rows.length > 1)) {
        const ri = part.rows.indexOf(row);
        if (ri >= 0) part.rows.splice(ri, 1);
      }
    }

    /** Parts whose content differs from the chain output — the confirm
     * payload. Blank rows are dropped; an emptied section is sent with
     * no rows (= remove its content from the created document). */
    function editedPartsPayload() {
      const out = [];
      for (const part of editableParts) {
        if (!part.editable || !part.part_id) continue;
        const rows = part.rows.map((r) => String(r.text ?? '').trim()).filter(Boolean);
        const changed =
          rows.length !== part.original.length || rows.some((t, i) => t !== part.original[i]);
        if (changed) out.push({ part_id: part.part_id, rows });
      }
      return out;
    }
    const creatingFilename = computed(() => props.block.props.creating?.filename || '');
    // Belt-and-braces: never render an attribute with an empty value —
    // an unplaced hierarchy reads as a mystery column ("Grade: null").
    const reviewAttributes = computed(() =>
      (review.value?.attributes || []).filter(
        (a) => a && String(a.value ?? '').trim() && String(a.value).toLowerCase() !== 'null',
      ),
    );
    const accept = computed(() =>
      Array.isArray(props.block.props.accept) && props.block.props.accept.length
        ? props.block.props.accept
        : ['.pdf', '.docx', '.txt', '.md'],
    );
    const acceptAttr = computed(() => accept.value.join(','));
    const acceptLabel = computed(() => accept.value.join(' · '));
    const originalIcon = computed(() => {
      const kind = review.value?.original?.kind;
      if (kind === 'pdf') return 'mdi-file-pdf-box';
      if (kind === 'docx') return 'mdi-file-word-box';
      return 'mdi-file-document-outline';
    });

    async function uploadFile(file, { actionLabel } = {}) {
      if (!file || uploading.value || !props.interactive) return;
      error.value = '';
      uploading.value = true;
      pendingName.value = file.name;
      try {
        const form = new FormData();
        form.append('file', file);
        const resp = await axios.post('/agent/upload?persist=true', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = resp.data || {};
        if (!data.file_id) throw new Error('no file_id returned');
        emit('action', {
          key: 'uploaded',
          label: actionLabel || `Uploaded ${data.filename || file.name}`,
          value: {
            file_id: data.file_id,
            filename: data.filename || file.name,
            kind: data.kind || '',
          },
        });
      } catch (err) {
        const msg = err?.response?.data?.message;
        error.value = msg || 'Upload failed — check the file type and try again.';
      } finally {
        uploading.value = false;
        pendingName.value = '';
      }
    }

    /**
     * First non-empty pasted line → a provenance-friendly filename
     * ("Senior Project Manager.txt") for the stored .txt — the first
     * line of an advert or profile is almost always the job title.
     * Falls back to a generic name when the line yields nothing usable.
     */
    function pastedFilename(text) {
      const firstLine =
        text.split(/\r?\n/).map((l) => l.trim()).find(Boolean) || '';
      const cleaned = firstLine
        .replace(/[\\/:*?"<>|]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 60)
        .trim();
      return `${cleaned || 'Pasted text'}.txt`;
    }

    function submitPastedText() {
      if (!pasteReady.value || uploading.value || !props.interactive) return;
      const text = pastedText.value.trim();
      const filename = pastedFilename(text);
      const file = new File([text], filename, { type: 'text/plain' });
      uploadFile(file, { actionLabel: `Pasted text: ${filename}` });
    }

    function onDrop(event) {
      dragOver.value = false;
      const file = event.dataTransfer?.files?.[0];
      uploadFile(file);
    }

    function onPick(event) {
      const file = event.target?.files?.[0];
      // Reset so picking the same file again re-fires change.
      event.target.value = '';
      uploadFile(file);
    }

    function confirm() {
      if (!props.interactive) return;
      const edits = editedPartsPayload();
      emit('action', {
        key: 'confirmed',
        label: edits.length ? 'Create the document with my edits' : 'Create the document',
        ...(edits.length ? { value: { edited_parts: edits } } : {}),
      });
    }

    function discard() {
      if (!props.interactive) return;
      emit('action', { key: 'discarded', label: 'Discard this draft' });
    }

    // Duplicate rows: clicking the row opens the existing document's
    // summary popover; the Open button opens the standard in-app view;
    // Compare runs the standard side-by-side over the listed documents —
    // the user can eyeball the incumbents before creating another.
    function openDuplicate(event, d) {
      if (!d || !d.reference) return;
      emit('open-summary', event, {
        reference: d.reference,
        doc_id: d.doc_id ?? null,
        doc_name: d.name ?? null,
      });
    }

    function openDuplicateDoc(d) {
      if (!d || !d.reference) return;
      emit('open-document', {
        reference: d.reference,
        doc_id: d.doc_id ?? null,
        doc_name: d.name ?? null,
      });
    }

    /** Badge text for the engine's name-match confidence band. */
    const matchLabel = matchTierLabel;

    /** Here the comparison is against the UPLOADED document's name, not
     * something the user typed. */
    function matchTitle(tier) {
      return matchTierTitle(tier, 'the name of the document you uploaded');
    }

    // "Why it matches" disclosure state, keyed per row. Reassigned (not
    // mutated) so Vue 2.7's reactivity sees new keys.
    const whyOpen = ref({});
    function whyKey(doc) {
      return String(doc.doc_id ?? doc.reference);
    }
    function toggleWhy(doc) {
      const k = whyKey(doc);
      whyOpen.value = { ...whyOpen.value, [k]: !whyOpen.value[k] };
    }
    function isWhyOpen(doc) {
      return !!whyOpen.value[whyKey(doc)];
    }

    function compareDuplicates() {
      const docs = (review.value?.duplicates || []).filter((d) => d && d.doc_id);
      if (docs.length < 2) return;
      emit('compare-documents', docs.map((d) => ({
        doc_id: d.doc_id,
        reference: d.reference,
        doc_name: d.name,
      })));
    }

    return {
      dragOver,
      uploading,
      pendingName,
      error,
      activeTab,
      inputMode,
      pastedText,
      pasteReady,
      submitPastedText,
      phase,
      review,
      editableParts,
      addRow,
      endRowEdit,
      partPreview,
      reviewAttributes,
      creatingFilename,
      acceptAttr,
      acceptLabel,
      originalIcon,
      onDrop,
      onPick,
      confirm,
      discard,
      openDuplicate,
      openDuplicateDoc,
      compareDuplicates,
      duplicateRows,
      matchLabel,
      matchTitle,
      // Status-dot colour (shared prototype STATUS_COLOR mapping).
      statusColor: docStatusColor,
      // "Why it matches" disclosure.
      toggleWhy,
      isWhyOpen,
    };
  },
});
</script>

<style scoped>
/* Prototype Surface treatment: quiet white card — hairline grey
   border, 16px radius, whisper of a shadow. */
.ui-docupload {
  margin-top: 12px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(229, 231, 235, 0.9);
  background: white;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
/* Card heading (DS Eyebrow): small, semibold, quiet grey. */
.ui-docupload__prompt {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgb(156 163 175);
  margin-bottom: 12px;
}
.ui-docupload__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 22px 16px;
  border: 2px dashed rgb(165 180 252);
  border-radius: 10px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  color: rgb(55 65 81);
  text-align: center;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.ui-docupload__dropzone--over {
  border-color: rgb(79 70 229);
  background: rgb(238 242 255);
}
.ui-docupload__dropzone--busy {
  cursor: progress;
  opacity: 0.8;
}
.ui-docupload__hint {
  font-size: 11px;
  color: rgb(107 114 128);
}
.ui-docupload__file-input {
  display: none;
}
/* Link-styled switch between the dropzone and the paste textarea. */
.ui-docupload__mode-toggle {
  display: block;
  margin: 8px auto 0;
  padding: 2px 6px;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: rgb(79 70 229);
  cursor: pointer;
}
.ui-docupload__mode-toggle:hover:not(:disabled) {
  text-decoration: underline;
}
.ui-docupload__mode-toggle:disabled {
  opacity: 0.5;
  cursor: default;
}
.ui-docupload__paste-input {
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 120px;
  font-size: 12.5px;
  line-height: 1.45;
}
.ui-docupload__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgb(199 210 254);
  border-top-color: rgb(79 70 229);
  border-radius: 50%;
  animation: ui-docupload-spin 0.8s linear infinite;
}
@keyframes ui-docupload-spin {
  to { transform: rotate(360deg); }
}
.ui-docupload__error {
  margin-top: 8px;
  font-size: 12px;
  color: rgb(185 28 28);
}
.ui-docupload__dup-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.ui-docupload__dup-title {
  flex: 1 1 auto;
  font-size: 12px;
  color: rgb(146 64 14);
}
/* Quiet amber text action (DS QuietBtn in the warn context). */
.ui-docupload__dup-compare {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: rgb(180 83 9);
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-docupload__dup-compare:hover {
  color: rgb(146 64 14);
  background: none;
}
/* Match rows: divided list (DS MatchItem), the warn Surface supplies
   the containment. */
.ui-docupload__dup-row {
  display: flex;
  /* Top-aligned: the match pill + Open sit level with the title line
     even when the reasons list expands the row. */
  align-items: flex-start;
  gap: 10px;
  padding: 10px 6px;
  border-bottom: 1px solid rgb(243 244 246);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background 0.12s ease;
}
.ui-docupload__dup-row:last-child {
  border-bottom: 0;
}
/* Trailing column: match pill on top, "Why it matches" beneath it,
   right-aligned and level with the title line. */
.ui-docupload__dup-trail {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-top: 2px;
}
.ui-docupload__dup-row:hover {
  background: rgb(249 250 251);
}
.ui-docupload__dup-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
/* Top line: title + other-hierarchy context. */
.ui-docupload__dup-name {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.ui-docupload__dup-name-text {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: rgb(17 24 39);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ui-docupload__dup-ctx {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 12px;
  color: rgb(156 163 175);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Second line: status dot + status. */
.ui-docupload__dup-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 12px;
  color: rgb(107 114 128);
}
.ui-docupload__dup-dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
}
/* Quiet indigo text action (DS "Use this" affordance). */
.ui-docupload__dup-open {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: rgb(79 70 229);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-docupload__dup-open:hover {
  color: rgb(67 56 202);
  border-color: transparent;
  background: none;
  text-decoration: underline;
}
/* Quiet grey "View" — opens the quick-view popover. */
.ui-docupload__dup-view {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: rgb(156 163 175);
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-docupload__dup-view:hover {
  color: rgb(55 65 81);
  text-decoration: underline;
}
.ui-docupload__dup-view .v-icon {
  color: currentColor !important;
}
/* A touch more separation between the status and the actions. */
.ui-docupload__dup-sub .ui-docupload__dup-open {
  margin-left: 6px;
}
/* Pill tabs (DS UploadMapCard): active tab wears an indigo wash; the
   right-aligned meta line orients ("6 mapped · tap to review"). */
.ui-docupload__tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
}
.ui-docupload__tab {
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: rgb(107 114 128);
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.ui-docupload__tab:hover {
  color: rgb(31 41 55);
}
.ui-docupload__tab--active {
  color: rgb(67 56 202);
  background: rgb(238 242 255);
}
.ui-docupload__tabs-meta {
  margin-left: auto;
  font-size: 11px;
  color: rgb(107 114 128);
}
.ui-docupload__creating {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 4px;
  font-size: 13px;
  color: rgb(55 65 81);
}
/* Duplicate panel: the prototype's WARN Surface — white with an
   amber hairline edge (DS `Surface tone="warn"`), the amber head line
   carrying the consequence. */
.ui-docupload__duplicates {
  padding: 12px 14px;
  margin-bottom: 12px;
  border-radius: 12px;
  border: 1px solid rgba(253, 230, 138, 0.8);
  background: white;
  font-size: 12px;
}
/* Family heading inside the warn panel — the shared grey eyebrow
   reads fine on the white surface; just breathing room. */
.ui-docupload__dup-group {
  margin-top: 8px;
}
.ui-docupload__attrs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 6px 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgb(243 244 246);
  margin-bottom: 8px;
}
/* Raw-data labels (DS UploadMapCard raw tab): small, semibold, quiet. */
.ui-docupload__attr-label {
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgb(107 114 128);
}
.ui-docupload__attr-value {
  font-size: 12.5px;
  color: rgb(31 41 55);
}
.ui-docupload__parts {
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
}
/* Each mapped section is a quiet rounded card (DS UploadMapCard field
   row); the open card sits a shade deeper. */
.ui-docupload__part {
  margin-bottom: 6px;
  border: 1px solid rgb(243 244 246);
  border-radius: 12px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.ui-docupload__part--open {
  border-color: rgb(229 231 235);
  background: rgba(249, 250, 251, 0.5);
}
.ui-docupload__part-head {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease;
}
.ui-docupload__part-head:hover {
  background: rgba(243, 244, 246, 0.6);
}
.ui-docupload__part-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.ui-docupload__part-name {
  font-size: 13px;
  font-weight: 500;
  color: rgb(17 24 39);
}
.ui-docupload__part-chevron {
  margin-left: auto;
  color: rgb(156 163 175) !important;
}
/* One-line preview while collapsed. */
.ui-docupload__part-preview {
  max-width: 100%;
  font-size: 12px;
  color: rgb(107 114 128);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Editors sit under a hairline inside the open card. */
.ui-docupload__part-body {
  padding: 6px 12px 12px;
  border-top: 1px solid rgb(243 244 246);
}
.ui-docupload__row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 6px;
}
.ui-docupload__drag {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding-top: 7px;
  cursor: grab;
}
.ui-docupload__drag:active {
  cursor: grabbing;
}
.ui-docupload__row-input {
  flex: 1 1 auto;
  resize: vertical;
  min-height: 34px;
}
/* Static row text — click to edit. Sized to match the textarea so the
   swap doesn't jump. */
.ui-docupload__row-static {
  flex: 1 1 auto;
  min-height: 34px;
  box-sizing: border-box;
  padding: 7px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 12.5px;
  color: rgb(31 41 55);
  white-space: pre-wrap;
  cursor: text;
}
.ui-docupload__row-static:hover,
.ui-docupload__row-static:focus-visible {
  outline: none;
  border-color: rgb(199 210 254);
  background: white;
}
.ui-docupload__row-static--empty {
  font-style: italic;
  color: rgb(156 163 175);
}
.ui-docupload__row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 6px;
  padding: 0;
  border: 1px solid rgb(229 231 235);
  border-radius: 6px;
  background: white;
  cursor: pointer;
}
.ui-docupload__row-btn:hover:not(:disabled) {
  border-color: rgb(165 180 252);
  background: rgb(238 242 255);
}
.ui-docupload__row-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.ui-docupload__add-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: 1px dashed rgb(165 180 252);
  border-radius: 6px;
  background: white;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: rgb(79 70 229);
  cursor: pointer;
}
.ui-docupload__add-row:hover {
  background: rgb(238 242 255);
}
.ui-docupload__part-text {
  font-size: 12.5px;
  color: rgb(31 41 55);
  white-space: pre-wrap;
}
.ui-docupload__items {
  margin: 2px 0 0;
  padding-left: 18px;
  font-size: 12.5px;
  color: rgb(31 41 55);
}
.ui-docupload__items li {
  margin-bottom: 2px;
}
.ui-docupload__truncated {
  font-size: 11px;
  font-style: italic;
  color: rgb(107 114 128);
}
.ui-docupload__source-label {
  margin-top: 10px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgb(107 114 128);
}
/* The document as Atlas read it — a quiet grey well (DS raw tab). */
.ui-docupload__source-text {
  margin-top: 4px;
  max-height: 260px;
  overflow-y: auto;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: rgb(249 250 251);
  font-size: 12.5px;
  line-height: 1.55;
  color: rgb(75 85 99);
  white-space: pre-wrap;
}
.ui-docupload__original {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  color: rgb(75 85 99);
}
/* Footer: DS PrimaryBtn + GhostBtn shapes. */
.ui-docupload__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
.ui-docupload__confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: rgb(79 70 229);
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;
}
.ui-docupload__confirm:hover:not(:disabled) {
  background: rgb(67 56 202);
}
.ui-docupload__confirm:disabled {
  opacity: 0.4;
  cursor: default;
}
.ui-docupload__discard {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgb(209 213 219);
  border-radius: 8px;
  background: white;
  color: rgb(55 65 81);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;
}
.ui-docupload__discard:hover {
  background: rgb(249 250 251);
}
.ui-docupload__inert {
  font-size: 12px;
  color: rgb(107 114 128);
}
</style>

<!-- Shared field-input look for the row editors — see uiBlockShared.css. -->
<style src="./uiBlockShared.css"></style>
